import React, { useState, useEffect } from 'react';
import './BookingForm.css';

import { Heading } from '../../components';

import axios from 'axios';
import { FaUser, FaEnvelope, FaPhoneAlt, FaCalendar, FaClock, FaUsers, FaSuitcase, FaCreditCard, FaAngleDown, FaComment, FaTaxi } from 'react-icons/fa';
import { FaLocationDot, FaMapLocationDot } from 'react-icons/fa6';


// API endpoint for submitting the booking form
const API_PATH = 'https://swiftride-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const BookingForm = ({ formType }) => {
  // State to manage form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    pickupLocation: '',
    dropoffLocation: '',
    date: '',
    time: '',
    passengers: '',
    luggage: '',
    taxiType: '',
    paymentMethod: '',
    instructions: ''
  });

  const [mailSent, setMailSent] = useState(false); // State to track if the email has been sent successfully
  const [error, setError] = useState(''); // State to handle error messages
  const [isSubmitting, setIsSubmitting] = useState(false); // State to manage form submission loading state

  // Effect to reset the mailSent state after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Function to handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      // Validate form fields
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.pickupLocation.trim()) validationErrors.push('Pickup location is required');
      if (!formData.dropoffLocation.trim()) validationErrors.push('Dropoff location is required');
      if (!formData.date.trim()) validationErrors.push('Pickup date is required');
      if (!formData.time.trim()) validationErrors.push('Pickup time is required');
      if (!formData.passengers) validationErrors.push('Number of passengers is required');
      if (!formData.luggage) validationErrors.push('Luggage option is required');
      if (!formData.taxiType) validationErrors.push('Taxi type is required');
      if (!formData.paymentMethod) validationErrors.push('Payment method is required');

      // If there are validation errors, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Submit form data to the API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Booking request submission failed');
      }

      // Reset form and set mailSent to true on success
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        pickupLocation: '',
        dropoffLocation: '',
        date: '',
        time: '',
        passengers: '',
        luggage: '',
        taxiType: '',
        paymentMethod: '',
        instructions: ''
      });
    } catch (err) {
      // Handle errors
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset submitting state
      setIsSubmitting(false);
    }
  };

  // Function to handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Render the form structure based on the `formType` prop
  const renderFormStructure = () => {
    if (formType === 'booking-page') {
      return (
        <>

          {/* Form heading */}
          <Heading title={'Request a Booking'} subTitle={'Book Your Ride'} sideHeading linear />

          <div className="input-box">

            {/* Name Input */}
            <div className="input-field">
              <label htmlFor="name"><FaUser /></label>
              <input type="text" className="box" id="name" name="name"
                value={formData.name} onChange={handleInputChange}
                placeholder="Full Name" disabled={isSubmitting} required />
            </div>

            {/* Email Input */}
            <div className="input-field">
              <label htmlFor="email"><FaEnvelope /></label>
              <input type="email" className="box" id="email" name="email"
                value={formData.email} onChange={handleInputChange}
                placeholder="Email Address" disabled={isSubmitting} required />
            </div>

          </div>

          <div className="input-box">

            {/* Phone Number Input */}
            <div className="input-field">
              <label htmlFor="number"><FaPhoneAlt /></label>
              <input type="tel" className="box" id="number" name="number"
                value={formData.number} onChange={handleInputChange}
                placeholder="Phone Number" disabled={isSubmitting} required />
            </div>

            {/* Pickup Location Input */}
            <div className="input-field">
              <label htmlFor="pickup-location"><FaLocationDot /></label>
              <input type="text" className="box" id="pickup-location" name="pickupLocation"
                value={formData.pickupLocation} onChange={handleInputChange}
                placeholder="Pickup Location" disabled={isSubmitting} required />
            </div>

          </div>

          <div className="input-box">

            {/* Dropoff Location Input */}
            <div className="input-field">
              <label htmlFor="dropoff-location"><FaMapLocationDot /></label>
              <input type="text" className="box" id="dropoff-location" name="dropoffLocation"
                value={formData.dropoffLocation} onChange={handleInputChange}
                placeholder="Dropoff Location" disabled={isSubmitting} required />
            </div>

          </div>

          <div className="input-box">

            {/* Pickup Date Input */}
            <div className="input-field">
              <label htmlFor="date"><FaCalendar /></label>
              <input type="text" className="box" id="date" name="date"
                value={formData.date} onChange={handleInputChange}
                placeholder="Pickup Date" onFocus={(e) => (e.target.type = 'date')}
                onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
            </div>

            {/* Pickup Time Input */}
            <div className="input-field">
              <label htmlFor="time"><FaClock /></label>
              <input type="text" className="box" id="time" name="time"
                value={formData.time} onChange={handleInputChange}
                placeholder="Pickup Time" onFocus={(e) => (e.target.type = 'time')}
                onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
            </div>

          </div>

          <div className="input-box">

            {/* Number of Passengers Input */}
            <div className="input-field">
              <label htmlFor="passengers"><FaUsers /></label>
              <input type="number" className="box" id="passengers" name="passengers"
                value={formData.passengers} onChange={handleInputChange}
                placeholder="No of Passengers" disabled={isSubmitting} required />
            </div>

            {/* Luggage Option Select */}
            <div className="select">
              <div className="select-container">
                <label htmlFor="luggage"><FaSuitcase /></label>
                <select className="select-box" id="luggage" name="luggage"
                  value={formData.luggage} onChange={handleInputChange}
                  disabled={isSubmitting} required>
                  <option value="">Luggage</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                <div className="icon-container"><FaAngleDown className="icon" /></div>
              </div>
            </div>

          </div>

          <div className="input-box">

            {/* Taxi Type Select */}
            <div className="select">
              <div className="select-container">
                <label htmlFor="taxi-type"><FaTaxi /></label>
                <select className="select-box" id="taxi-type" name="taxiType"
                  value={formData.taxiType} onChange={handleInputChange}
                  disabled={isSubmitting} required>
                  <option value="">Taxi Type</option>
                  <option value="economy">Economy Taxi</option>
                  <option value="luxury">Luxury Taxi</option>
                  <option value="shared-ride">Shared Ride</option>
                  <option value="airport-taxi">Airport Taxi</option>
                  <option value="business-taxi">Business Taxi</option>
                  <option value="minivan-taxi">Minivan Taxi</option>
                </select>
                <div className="icon-container"><FaAngleDown className="icon" /></div>
              </div>
            </div>

            {/* Payment Method Select */}
            <div className="select">
              <div className="select-container">
                <label htmlFor="payment-method"><FaCreditCard /></label>
                <select className="select-box" id="payment-method" name="paymentMethod"
                  value={formData.paymentMethod} onChange={handleInputChange}
                  disabled={isSubmitting} required>
                  <option value="">Payment Method</option>
                  <option value="credit-card">Credit Card</option>
                  <option value="cash">Cash</option>
                  <option value="mobile-payment">Mobile Payment</option>
                </select>
                <div className="icon-container"><FaAngleDown className="icon" /></div>
              </div>
            </div>

          </div>

          {/* Special Instructions Text Area */}
          <div className="input-field text-area">
            <label htmlFor="instructions"><FaComment /></label>
            <textarea cols="30" rows="10" id="instructions" className="box"
              name="instructions" value={formData.instructions} onChange={handleInputChange}
              placeholder="Special Instructions" disabled={isSubmitting} required></textarea>
          </div>

          {/* Submit Button */}
          <button type="submit" className="btn" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Book Ride'}
          </button>

        </>
      );
    } else if (formType === 'home-booking') {
      return (
        <>
          {/* Form heading */}
          <Heading title={'Request a Booking'} subTitle={'Book Your Ride'} sideHeading linear />

          <div className="input-box">

            {/* Name Input */}
            <div className="input-field">
              <label htmlFor="name"><FaUser /></label>
              <input type="text" className="box" id="name" name="name"
                value={formData.name} onChange={handleInputChange}
                placeholder="Full Name" disabled={isSubmitting} required />
            </div>

            {/* Email Input */}
            <div className="input-field">
              <label htmlFor="email"><FaEnvelope /></label>
              <input type="email" className="box" id="email" name="email"
                value={formData.email} onChange={handleInputChange}
                placeholder="Email Address" disabled={isSubmitting} required />
            </div>

            {/* Phone Number Input */}
            <div className="input-field">
              <label htmlFor="number"><FaPhoneAlt /></label>
              <input type="tel" className="box" id="number" name="number"
                value={formData.number} onChange={handleInputChange}
                placeholder="Phone Number" disabled={isSubmitting} required />
            </div>

            {/* Pickup Location Input */}
            <div className="input-field">
              <label htmlFor="pickup-location"><FaLocationDot /></label>
              <input type="text" className="box" id="pickup-location" name="pickupLocation"
                value={formData.pickupLocation} onChange={handleInputChange}
                placeholder="Pickup Location" disabled={isSubmitting} required />
            </div>

          </div>

          <div className="input-box">

            {/* Dropoff Location Input */}
            <div className="input-field">
              <label htmlFor="dropoff-location"><FaMapLocationDot /></label>
              <input type="text" className="box" id="dropoff-location" name="dropoffLocation"
                value={formData.dropoffLocation} onChange={handleInputChange}
                placeholder="Dropoff Location" disabled={isSubmitting} required />
            </div>

            {/* Pickup Date Input */}
            <div className="input-field">
              <label htmlFor="date"><FaCalendar /></label>
              <input type="text" className="box" id="date" name="date"
                value={formData.date} onChange={handleInputChange}
                placeholder="Pickup Date" onFocus={(e) => (e.target.type = 'date')}
                onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
            </div>

            {/* Pickup Time Input */}
            <div className="input-field">
              <label htmlFor="time"><FaClock /></label>
              <input type="text" className="box" id="time" name="time"
                value={formData.time} onChange={handleInputChange}
                placeholder="Pickup Time" onFocus={(e) => (e.target.type = 'time')}
                onBlur={(e) => (e.target.type = 'text')} disabled={isSubmitting} required />
            </div>

            {/* Number of Passengers Input */}
            <div className="input-field">
              <label htmlFor="passengers"><FaUsers /></label>
              <input type="number" className="box" id="passengers" name="passengers"
                value={formData.passengers} onChange={handleInputChange}
                placeholder="No of Passengers" disabled={isSubmitting} required />
            </div>

          </div>

          <div className="input-box">

            {/* Luggage Option Select */}
            <div className="select">
              <div className="select-container">
                <label htmlFor="luggage"><FaSuitcase /></label>
                <select className="select-box" id="luggage" name="luggage"
                  value={formData.luggage} onChange={handleInputChange}
                  disabled={isSubmitting} required>
                  <option value="">Luggage</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                <div className="icon-container"><FaAngleDown className="icon" /></div>
              </div>
            </div>

            {/* Taxi Type Select */}
            <div className="select">
              <div className="select-container">
                <label htmlFor="taxi-type"><FaTaxi /></label>
                <select className="select-box" id="taxi-type" name="taxiType"
                  value={formData.taxiType} onChange={handleInputChange}
                  disabled={isSubmitting} required>
                  <option value="">Taxi Type</option>
                  <option value="economy">Economy Taxi</option>
                  <option value="luxury">Luxury Taxi</option>
                  <option value="shared-ride">Shared Ride</option>
                  <option value="airport-taxi">Airport Taxi</option>
                  <option value="business-taxi">Business Taxi</option>
                  <option value="minivan-taxi">Minivan Taxi</option>
                </select>
                <div className="icon-container"><FaAngleDown className="icon" /></div>
              </div>
            </div>

            {/* Payment Method Select */}
            <div className="select">
              <div className="select-container">
                <label htmlFor="payment-method"><FaCreditCard /></label>
                <select className="select-box" id="payment-method" name="paymentMethod"
                  value={formData.paymentMethod} onChange={handleInputChange}
                  disabled={isSubmitting} required>
                  <option value="">Payment Method</option>
                  <option value="credit-card">Credit Card</option>
                  <option value="cash">Cash</option>
                  <option value="mobile-payment">Mobile Payment</option>
                </select>
                <div className="icon-container"><FaAngleDown className="icon" /></div>
              </div>
            </div>

            {/* Submit Button */}
            <div className='form-btn'>    
              <button type="submit" className="btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Book Ride'}
              </button>
            </div>

          </div>

        </>
      );
    }
  };

  return (
    <form className="dark-form" onSubmit={handleSubmitForm}>
      {renderFormStructure()}

      {error && (
        <div className="alert error">
          {error.split(' | ').map((msg, i) => (
            <span key={i}>{msg}</span>
          ))}
        </div>
      )}
      {mailSent && <div className="alert success"><span>Request submitted</span></div>}
    </form>
  );
};

export default BookingForm;